import { ChangeEvent, useEffect, useRef, useState } from "react";

/**
 * Hook that only runs once, allows for async usage
 */
type UseOnceReturnType = void | (() => void);
export const useOnce = (
  fn: () => UseOnceReturnType | Promise<UseOnceReturnType>
): void => {
  useEffect(() => {
    const cleanup = fn();
    if (typeof cleanup === "function") {
      return cleanup;
    }
    /* eslint-disable-next-line */
    return;
  }, []);
};

/**
 * https://usehooks.com/usePrevious/
 *
 * Hook to get the previous value of props or state
 */
/* istanbul ignore next */
export const usePrevious = <T extends Record<any, any>>(value: T): T => {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current as T;
};

/**
 * Hook to manage state of <TextField />
 */
export const useTextFieldState = (
  initialValue = "",
  initialHelperText = ""
) => {
  const [value, setValue] = useState<string>(initialValue);
  const [helperText, setHelperText] = useState<string>(initialHelperText);
  const [error, setError] = useState<boolean>(false);
  const [focus, setFocus] = useState<boolean>(false);

  return {
    error,
    focus,
    helperText,
    setError: (message: string) => {
      if (message) {
        setHelperText(message);
        setError(true);

        return;
      }

      setHelperText(initialHelperText);
      setError(false);
    },
    setFocus: (newFocus: boolean) => {
      setFocus(newFocus);
    },
    setValue: (eventOrValue: string | ChangeEvent<HTMLInputElement>) => {
      if (typeof eventOrValue === "string") {
        setValue(eventOrValue);
        return;
      }

      setValue(eventOrValue.target.value);
    },
    value,
  };
};
